import apiClient from '@/services/api';

/**
 * Optional audit context for download. Pass this only for user-initiated downloads
 * (e.g. clicking Download in the document store UI). When omitted, the backend skips
 * the audit trail entry — so internal/anonymous downloads (profile pics, etc.) don't
 * clutter the audit log.
 */
export interface DownloadAuditMeta {
  module: 'document_store' | (string & {});
  resource_id?: string;
  resource_name?: string;
  parent_id?: string;
  parent_name?: string;
}

/**
 * Download file from storage using storage key
 * Uses backend proxy with proper S3 credentials
 */
export async function downloadFile(
  key: string,
  filename: string,
  auditMeta?: DownloadAuditMeta
): Promise<void> {
  try {
    // Use backend proxy to fetch the file with authentication and S3 credentials
    const params: Record<string, string> = {
      key,
      filename,
    };
    if (auditMeta?.module) params.module = auditMeta.module;
    if (auditMeta?.resource_id) params.resource_id = auditMeta.resource_id;
    if (auditMeta?.resource_name) params.resource_name = auditMeta.resource_name;
    if (auditMeta?.parent_id) params.parent_id = auditMeta.parent_id;
    if (auditMeta?.parent_name) params.parent_name = auditMeta.parent_name;

    const response = await apiClient.get('/storage/utils/download', {
      params,
      responseType: 'blob', // Important: Get response as blob
    });

    // Create blob URL from response
    const blob = new Blob([response.data]);
    const blobUrl = window.URL.createObjectURL(blob);

    // Create temporary link and trigger download
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    link.style.display = 'none';

    // Append to body, click, and cleanup
    document.body.appendChild(link);
    link.click();

    // Cleanup
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    }, 100);
  } catch (error) {
    console.error('Download error:', error);
    throw error;
  }
}
